import PropTypes from 'prop-types';
import React, { useMemo, useEffect } from 'react';
import get from 'lodash.get';
import classNames from 'classnames';
import { COLUMNS_NAMES } from '../../../common/utils/studentsSortingHelpers';
import { IDEL2 } from '../../../constants/applications';
import {
    MEASURE_SCORE_DISPLAY_NAMES,
    IDEL2_SUBMEASURES,
    MEASURE_SCORES_BY_APPLICATION,
    SKILL_NAMES,
    MEASURE_NAMES,
    LECTURA_DATA_SOURCE_RESULTS_SCRIPT,
} from '../../../constants/assessments';
import { BM_PERIODS, GRADE } from '../../../constants/enrollment';
import { resultType } from '../../../constants/prop-types';
import AssessLink from '../assess-link/AssessLink';
import Idel2MeasureScore from './Idel2MeasureScore';
import { getGatingRules } from '../../../common/gating-rules/getGatingRules';
import { getMeasureScores } from '../../../common/utils/studentsListHelpers';
import { getMeasureScoreBySkillName } from '../../../common/utils/measureScoreBySkillName';
import {
    constructIdel2Link,
    PAGE_TYPE,
    linkOnClickHandler,
} from '../../../common/navigation/navigation';
import { usePopupContext } from '../../../common/contexts/PopupContext';
import { useAppContext } from '../../../common/contexts/AppContext';
import { useAppConfigContext } from '../../../common/contexts/AppConfigContext';
import MeasureBannersContainer from '../../banner/MeasureBannersContainer';
import InfoIcon from '../info-icon/InfoIcon';
import { useBannerContext } from '../../../common/contexts/BannerContext';
import TableHeader from '../table-header/TableHeader';
import HyphenIcon from '../hyphen-icon/HyphenIcon';
import CompositeScoreRow from '../composite-score-row/CompositeScoreRow';
import { hasAlternativeForms as getHasAlternativeForms } from '../../../common/utils/alternateFormsHelpers';
import useAssessIcons from '../../../common/hooks/useAssessIcons';
import { getBMRecommendationRules } from '../../../common/bm-recommendation-rules/bmRecommendationRules';
import { useFeatureToggleContext } from '../../../common/contexts/FeatureToggleContext';
import { getGatedMeasuresStatusForLectura } from '../../../common/gating-rules/getGatedMeasuresStatus';
import { handleEnterKeyPress } from '../../../common/popups/utils';

const DISABLED_ICONS = {
    [AssessLink.MODE.OPTIONAL]: AssessLink.MODE.OPTIONAL_DISABLED,
    [AssessLink.MODE.DISCONTINUE]: AssessLink.MODE.DISCONTINUE_DISABLED,
    [AssessLink.MODE.TESTED_OUT]: AssessLink.MODE.TESTED_OUT_DISABLED,
    [AssessLink.MODE.RECOMMENDED]: AssessLink.MODE.RECOMMENDED_DISABLED,
    [AssessLink.MODE.REQUIRED]: AssessLink.MODE.DISABLED,
    [AssessLink.MODE.DISABLED]: AssessLink.MODE.DISABLED,
};

const getAssessLinkMode = (
    bmRecommendationRules,
    gatingRules,
    measureName,
    isOptional,
    isLecturaGatingRulesEnabled,
) => {
    if (isLecturaGatingRulesEnabled) {
        const ruleMet = gatingRules.find(
            rule => rule.isRuleMet && rule.gatedMeasures.includes(measureName),
        );

        const ruleNotMet = gatingRules.find(
            rule => rule.isRuleMet === null && rule.gatedMeasures.includes(measureName),
        );

        if (ruleMet) {
            return AssessLink.MODE[ruleMet.ruleType];
        }

        if (ruleNotMet) {
            return AssessLink.MODE.DISABLED;
        }
    }

    if (bmRecommendationRules?.[measureName]?.isRuleMet) {
        return AssessLink.MODE.RECOMMENDED;
    }
    if (isOptional) {
        return AssessLink.MODE.OPTIONAL;
    }

    return AssessLink.MODE.REQUIRED;
};

const getIsGatedMeasure = (gatingRules, measureName) =>
    gatingRules.some(
        rule =>
            rule.gatedMeasures.includes(measureName) && (rule.isRuleMet || rule.isRuleMet === null),
    );

const isHyphenIcon = skillName =>
    SKILL_NAMES.COMPRENSION_BASICA === skillName || IDEL2_SUBMEASURES.includes(skillName);

const getSkillDetailsForPeriod = (
    skillName,
    measureName,
    period,
    isLecturaCompositeScoreEnabled,
    isLecturaPMEnabled,
    isLecturaGatingRulesEnabled,
    isBMAssessmentIconEnabled,
    isBMAssessmentIconVisible,
    popupActions,
    isOptional,
    { student, classId, results },
    formsMap,
) => {
    const { id, grade, appStatuses } = student;
    const measureScoreName = getMeasureScoreBySkillName(skillName, measureName);
    const assessmentLink = constructIdel2Link(id, measureName, PAGE_TYPE.REMINDERS, {
        period,
        classId,
    });
    const studentResults = get(results, [period, id], []);
    const gatedMeasuresStatus = getGatedMeasuresStatusForLectura(appStatuses, period);
    const gatingRules = getGatingRules(studentResults, grade, period, gatedMeasuresStatus, IDEL2);
    const measureScores = getMeasureScores(studentResults, measureName, measureScoreName);
    const key = `${skillName}${period}`;
    const isAssessmentEnabled = isBMAssessmentIconEnabled(period);

    const bmRecommendationRules = getBMRecommendationRules(studentResults);

    const isFromResultsScript =
        measureScores && measureScores.dataSource === LECTURA_DATA_SOURCE_RESULTS_SCRIPT;

    if (!measureScores && isBMAssessmentIconVisible(period)) {
        if (isHyphenIcon(skillName)) {
            return <HyphenIcon key={key} />;
        }

        const isGatedMeasure = getIsGatedMeasure(gatingRules, measureScoreName);

        const assessmentLinkMode = getAssessLinkMode(
            bmRecommendationRules,
            gatingRules,
            measureScoreName,
            isOptional,
            isLecturaGatingRulesEnabled,
        );

        const assessLinkMode = isAssessmentEnabled
            ? assessmentLinkMode
            : DISABLED_ICONS[assessmentLinkMode];

        const launchAlternateFormSelectionPopup = () => {
            popupActions.showLecturaAltFormSelectionPopup({
                grade,
                studentId: id,
                period,
                skillName,
                measureName,
            });
        };

        const hasAlternativeForms =
            isLecturaPMEnabled && getHasAlternativeForms(studentResults, measureName, IDEL2);

        if (isGatedMeasure && isLecturaGatingRulesEnabled) {
            if (
                [AssessLink.MODE.DISCONTINUE, AssessLink.MODE.TESTED_OUT].includes(assessLinkMode)
            ) {
                const startAssessment = linkOnClickHandler(assessmentLink);
                const launchGatedMeasureNoticePopup = () => {
                    popupActions.showGatedMeasureNoticePopup({
                        name: assessLinkMode,
                        onContinueClick: hasAlternativeForms
                            ? launchAlternateFormSelectionPopup
                            : startAssessment,
                    });
                };
                return (
                    <AssessLink
                        key={key}
                        mode={assessLinkMode}
                        onClickHandler={launchGatedMeasureNoticePopup}
                        onKeyDown={handleEnterKeyPress}
                    />
                );
            }
        }

        if (hasAlternativeForms) {
            return (
                <AssessLink
                    key={key}
                    mode={assessLinkMode}
                    onClickHandler={launchAlternateFormSelectionPopup}
                    onKeyDown={handleEnterKeyPress}
                />
            );
        }

        return <AssessLink key={key} mode={assessLinkMode} path={assessmentLink} />;
    }

    if (!measureScores) {
        return <div key={key} />;
    }

    const scoreInterpretation = isLecturaCompositeScoreEnabled ? measureScores.interpretation : '';

    return (
        <Idel2MeasureScore
            isFromResultsScript={isFromResultsScript}
            interpretation={scoreInterpretation}
            score={measureScores.rawScore}
            studentId={student.id}
            measure={measureName}
            period={period}
            classId={classId}
            resultId={measureScores.resultId}
            isFormAvailable={formsMap.hasOwnProperty(measureScores.formUid)}
            key={key}
        />
    );
};

const Idel2Summary = props => {
    const { student, classId, results } = props;
    const {
        bmWindow: { period: currentPeriod },
        formsMap,
    } = useAppContext();

    const { actions: popupActions } = usePopupContext();
    const {
        actions: { showBMCPBanner, enableCPBanner, enableFNLBanner, showFNLBanner },
    } = useBannerContext();
    const {
        isLecturaAssessmentEnabled,
        isLecturaCompositeScoreEnabled,
        isLecturaGatingRulesEnabled,
        isLecturaPMEnabled,
    } = useFeatureToggleContext();
    const { isBMAssessmentIconEnabled, isBMAssessmentIconVisible } = useAssessIcons();

    const idel2LicensedData = get(
        useAppConfigContext(),
        ['appConfigs', IDEL2, 'licensedMeasures'],
        [],
    );
    const sortedLicenses = useMemo(
        () =>
            MEASURE_SCORES_BY_APPLICATION[IDEL2].reduce((list, measureScore) => {
                const licensedMeasure = idel2LicensedData.find(
                    licensedItem =>
                        getMeasureScoreBySkillName(licensedItem.skillName, licensedItem.measure) ===
                            measureScore &&
                        !licensedItem.isInventory &&
                        licensedItem.grades.includes(student.grade),
                );
                if (licensedMeasure) {
                    return [...list, { ...licensedMeasure, measureScore }];
                }
                return list;
            }, []),
        [idel2LicensedData, student.grade],
    );

    const isCPAvailable = useMemo(
        () =>
            sortedLicenses.some(
                licensedItem => licensedItem.skillName === SKILL_NAMES.COMPRENSION_BASICA,
            ) &&
            !get(results, `${currentPeriod}.${student.id}`, []).some(
                result =>
                    result.measure === MEASURE_NAMES.CP && result.isComplete && result.isValid,
            ),
        [sortedLicenses, results, currentPeriod, student.id],
    );

    useEffect(() => {
        enableCPBanner({
            classId,
            studentUid: student.id,
            isAvailable: isCPAvailable,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isCPAvailable]);

    const isFNLAvailable = useMemo(
        () =>
            sortedLicenses.some(
                licensedItem => licensedItem.skillName === SKILL_NAMES.NOMBRAR_LETRAS,
            ) && student.grade === GRADE.TWO,
        [sortedLicenses, student.grade],
    );

    useEffect(() => {
        if (isFNLAvailable) {
            enableFNLBanner({
                classId,
                studentUid: student.id,
                isAvailable: isFNLAvailable,
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isFNLAvailable]);

    const showCPBanner = () => showBMCPBanner({ classId, studentUid: student.id });
    const showPMFNLBanner = () => showFNLBanner({ classId, studentUid: student.id });

    const headerTittle = isLecturaAssessmentEnabled ? 'Lectura' : COLUMNS_NAMES.IDEL2;
    // TODO: Remove ^^ this constant and use COLUMNS_NAMES.IDEL2 for TableHeader component
    // when isLecturaAssessmentEnabled is permanently enabled after 2022-07-01

    return (
        sortedLicenses.length > 0 && (
            <>
                <TableHeader columns={[headerTittle, ...BM_PERIODS]} />
                <MeasureBannersContainer studentUid={student.id} classId={classId} />
                {isLecturaCompositeScoreEnabled && (
                    <CompositeScoreRow appName={IDEL2} student={student} />
                )}
                <div className='dashboard-measure-scores' data-test-id="lectura-measure-scores">
                    {sortedLicenses.map(licensedItem => (
                        <div
                            className='dashboard-measure-info'
                            key={`${licensedItem.skillName}${licensedItem.measure}`}
                        >
                            <div className='dashboard-skill'>
                                <div className='dashboard-measure-container'>
                                    <span
                                        className={classNames('dashboard-skill-name', {
                                            'dashboard-optional-skill':
                                                licensedItem.measure === MEASURE_NAMES.FSLIK ||
                                                licensedItem.measure === MEASURE_NAMES.FSLI,
                                        })}
                                    >
                                        {licensedItem.skillName}
                                    </span>
                                    <span className='dashboard-measure-name'>
                                        {MEASURE_SCORE_DISPLAY_NAMES[licensedItem.measureScore]}
                                    </span>
                                </div>
                                {licensedItem.skillName === SKILL_NAMES.COMPRENSION_BASICA &&
                                    isCPAvailable && <InfoIcon onClickHandler={showCPBanner} />}
                                {licensedItem.skillName === SKILL_NAMES.NOMBRAR_LETRAS &&
                                    isFNLAvailable && <InfoIcon onClickHandler={showPMFNLBanner} />}
                            </div>

                            {BM_PERIODS.map(period =>
                                getSkillDetailsForPeriod(
                                    licensedItem.skillName,
                                    licensedItem.measure,
                                    period,
                                    isLecturaCompositeScoreEnabled,
                                    isLecturaPMEnabled,
                                    isLecturaGatingRulesEnabled,
                                    isBMAssessmentIconEnabled,
                                    isBMAssessmentIconVisible,
                                    popupActions,
                                    licensedItem?.isOptional,
                                    {
                                        student,
                                        classId,
                                        results,
                                    },
                                    formsMap,
                                ),
                            )}
                        </div>
                    ))}
                </div>
                <div className='dashboard-empty-divider' />
            </>
        )
    );
};

Idel2Summary.propTypes = {
    student: PropTypes.object.isRequired,
    classId: PropTypes.string.isRequired,
    results: PropTypes.shape({
        BOY: PropTypes.objectOf(PropTypes.arrayOf(resultType)),
        MOY: PropTypes.objectOf(PropTypes.arrayOf(resultType)),
        EOY: PropTypes.objectOf(PropTypes.arrayOf(resultType)),
        PM: PropTypes.objectOf(PropTypes.arrayOf(resultType)),
    }),
};

Idel2Summary.defaultProps = {
    results: {},
};

export default Idel2Summary;
